import { Checkbox, FormControlLabel, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import type {
  Facility,
  SelectedState,
  PartnerFacilitiesFilter,
} from '../../API/services/PartnerFacilityFilterService';
import CustomTypography from '@ui-components/Typography/CustomTypography';

type Props = Readonly<{
  data: PartnerFacilitiesFilter | undefined;
  showPartnerCheckbox?: boolean;
  singleFacilitySelection?: boolean;
  value?: SelectedState;
  onChange?: (value: SelectedState) => void;
}>;

export function PartnerFacilitySelector({
  data,
  showPartnerCheckbox = true,
  singleFacilitySelection = false,
  value,
  onChange,
}: Props) {
  const [internalValue, setInternalValue] = useState<SelectedState>(
    value ?? {}
  );

  const selected = value ?? internalValue;

  // Sync when controlled
  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  const updateValue = (next: SelectedState) => {
    if (!value) {
      setInternalValue(next);
    }
    onChange?.(next);
  };

  // ---- Partner toggle ----
  const handlePartnerChange = (
    partnerId: number,
    facilities: Facility[],
    checked: boolean
  ) => {
    updateValue({
      ...selected,
      [partnerId]: checked ? facilities.map((f) => f.id) : [],
    });
  };

  const handleFacilityChange = (
    partnerId: number,
    facilityId: number,
    checked: boolean
  ) => {
    if (singleFacilitySelection) {
      const next: SelectedState = {};

      if (checked) {
        next[partnerId] = [facilityId];
      }

      updateValue(next);
      return;
    }

    const current = selected[partnerId] || [];

    const nextFacilities = checked
      ? [...current, facilityId]
      : current.filter((id) => id !== facilityId);

    updateValue({
      ...selected,
      [partnerId]: nextFacilities,
    });
  };

  return (
    <Box className="scroller-box">
      {data?.partnerFacilitiesMap && data?.partnerFacilitiesMap.length > 0 ? (
        data?.partnerFacilitiesMap.map((partner) => {
          const selectedFacilities = selected[partner.partnerId] || [];
          const allSelected =
            selectedFacilities.length === partner.facilities.length;
          const isIndeterminate = selectedFacilities.length > 0 && !allSelected;

          return (
            <Box key={partner.partnerId}>
              {/* Partner */}
              {showPartnerCheckbox ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={isIndeterminate}
                      onChange={(e) =>
                        handlePartnerChange(
                          partner.partnerId,
                          partner.facilities,
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={
                    <CustomTypography
                      id="partner-name-withCheckbox"
                      variant="body2"
                    >
                      {partner.partnerName}
                    </CustomTypography>
                  }
                />
              ) : (
                <CustomTypography
                  id="partner-name-withoutCheckbox"
                  variant="body2"
                >
                  {partner.partnerName}
                </CustomTypography>
              )}

              {/* Facilities */}
              <Box
                className={`flex flex-direction-column pt-4 ${
                  showPartnerCheckbox ? 'pl-24' : ''
                }`}
              >
                {partner.facilities.length > 0 ? (
                  partner.facilities.map((facility) => (
                    <FormControlLabel
                      key={facility.id}
                      control={
                        <Checkbox
                          checked={selectedFacilities.includes(facility.id)}
                          onChange={(e) =>
                            handleFacilityChange(
                              partner.partnerId,
                              facility.id,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label={facility.name}
                    />
                  ))
                ) : (
                  <CustomTypography
                    id={`no-facilities-${partner.partnerId}`}
                    variant="body3"
                    color="textSecondary"
                    className="pl-12"
                  >
                    No Facilities Available
                  </CustomTypography>
                )}
              </Box>
            </Box>
          );
        })
      ) : (
        <CustomTypography
          id={`no-partner`}
          variant="body3"
          color="textSecondary"
          className="pl-12"
        >
          No Partner Available
        </CustomTypography>
      )}
    </Box>
  );
}

export default PartnerFacilitySelector;
